import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { sortBy, uniqBy } from "lodash-es";
import { Component } from "react";
import { FocusScope } from "react-aria";
import { connect } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError, type Validator } from "redux-form";

import { type ISelectOption, simpleFieldValidation, specialCharacterValidation } from "@/app-utils/form.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import {
  type InputOrganismPropertyGroupInput,
  type InputOrganismPropertyInput,
  type InputOrganismPropertyTypeInput,
  type OrganismProperty,
  type OrganismPropertyType,
} from "@/graphql.ts";
import { loadAllOrgPropertyTypes, loadOrgPropertyType, saveOrgPropertyType } from "@/redux/modules/data-editing.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { DEFAULT_ORG_PROPERTY_GROUP, ORG_PROPERTY_VALUE_TYPE } from "@/settings/settings.ts";

import ControlledValuesManager, { type IInputVocabularyTerm } from "../admin/ControlledValuesManager.js";
import { Dialog } from "./overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "./redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "./redux-form/ReactSelectField.js";

export interface INewPhenotypeTagDialogOwnProps {
  propertyTypeIdToEdit?: number;
  close?: () => void;
}

interface INewPhenotypeTagDialogState {}

interface INewPhenotypeTagDialogAppStateProps {
  currentTypeSelection: ISelectOption;
  currentGroupName: string;
  currentDefaultTermSelection: IInputVocabularyTerm[];
  existingOrgPropertyTypes: OrganismPropertyType[];
  orgPropertyValueLookup: Dictionary<OrganismProperty[]>;
  isLoadingOrgPropertyTypes: boolean;
  initialValues: Partial<any>;
}

interface INewPhenotypeTagDialogDispatchProps {
  saveOrgPropertyType: (payload: InputOrganismPropertyTypeInput) => Promise<OrganismPropertyType>;
  loadAllOrgPropertyTypes: () => void;
  loadOrgPropertyType: (orgPropertyTypeId: number) => Promise<OrganismPropertyType>;
  //loadExistingOrgPropertyGroups: () => Promise<Array<IOrgPropertyGroup>>;
}

export interface INewPhenotypeTagDialogProps
  extends INewPhenotypeTagDialogAppStateProps,
    INewPhenotypeTagDialogDispatchProps,
    INewPhenotypeTagDialogOwnProps,
    InjectedFormProps<any, any> {}

const VALUE_TYPE_OPTIONS: ISelectOption[] = [
  { label: "Text", value: ORG_PROPERTY_VALUE_TYPE.FREE },
  { label: "Number", value: ORG_PROPERTY_VALUE_TYPE.NUMERIC },
];

function mapStateToProps(
  state: IAppState,
  _ownProps: INewPhenotypeTagDialogProps,
): INewPhenotypeTagDialogAppStateProps {
  return {
    currentTypeSelection: formValueSelector(NewPhenotypeTagDialogImpl.FORM_NAME)(
      state,
      NewPhenotypeTagDialogImpl.FIELD_TYPE,
    ),
    currentGroupName: formValueSelector(NewPhenotypeTagDialogImpl.FORM_NAME)(
      state,
      NewPhenotypeTagDialogImpl.FIELD_GROUP_NAME,
    ),
    currentDefaultTermSelection: formValueSelector(NewPhenotypeTagDialogImpl.FORM_NAME)(
      state,
      NewPhenotypeTagDialogImpl.FIELD_TEXT_VALUES,
    ),
    existingOrgPropertyTypes: state.dataEditing.orgPropertyTypes,
    orgPropertyValueLookup: state.dataEditing.orgPropertyValuesLookup,
    isLoadingOrgPropertyTypes: state.dataEditing.isLoadingOrgPropertyTypes,
    initialValues: {
      [NewPhenotypeTagDialogImpl.FIELD_NAME]: null,
      [NewPhenotypeTagDialogImpl.FIELD_DESCRIPTION]: null,
      [NewPhenotypeTagDialogImpl.FIELD_GROUP_NAME]: null,
      [NewPhenotypeTagDialogImpl.FIELD_TYPE]: VALUE_TYPE_OPTIONS[0],
      [NewPhenotypeTagDialogImpl.FIELD_TEXT_VALUES]: [],
    },
  };
}

function mapDispatchToProps(dispatch): INewPhenotypeTagDialogDispatchProps {
  return {
    saveOrgPropertyType: (payload: InputOrganismPropertyTypeInput) => dispatch(saveOrgPropertyType(payload)),
    loadAllOrgPropertyTypes: () => dispatch(loadAllOrgPropertyTypes()),
    loadOrgPropertyType: (orgPropertyTypeId: number) => dispatch(loadOrgPropertyType(orgPropertyTypeId)),
  };
}

class NewPhenotypeTagDialogImpl extends Component<INewPhenotypeTagDialogProps, INewPhenotypeTagDialogState> {
  public static FORM_NAME = "newPhenotypeTag";

  public static FIELD_NAME = "FIELD_NAME";
  public static FIELD_DESCRIPTION = "FIELD_DESCRIPTION";
  public static FIELD_GROUP_NAME = "FIELD_GROUP_NAME";
  public static FIELD_TYPE = "FIELD_TYPE";
  public static FIELD_TEXT_VALUES = "FIELD_TEXT_VALUES";

  state = {};

  componentDidMount() {
    this.props.loadAllOrgPropertyTypes();
    if (this.props.propertyTypeIdToEdit) {
      this.props.loadOrgPropertyType(this.props.propertyTypeIdToEdit).then((orgPropertyType: OrganismPropertyType) => {
        this.props.change(NewPhenotypeTagDialogImpl.FIELD_NAME, orgPropertyType.name);
        this.props.change(NewPhenotypeTagDialogImpl.FIELD_DESCRIPTION, orgPropertyType.description);
        this.props.change(NewPhenotypeTagDialogImpl.FIELD_GROUP_NAME, {
          label: orgPropertyType.group.name,
          value: orgPropertyType.group.name,
        });
        this.props.change(
          NewPhenotypeTagDialogImpl.FIELD_TYPE,
          VALUE_TYPE_OPTIONS.find((v) => v.value === orgPropertyType.valueType),
        );
        this.props.change(
          NewPhenotypeTagDialogImpl.FIELD_TEXT_VALUES,
          // either load the voc terms from the data-editing redux state (including freshly created ones)...
          this.props.orgPropertyValueLookup[orgPropertyType.id].map((p) => {
            // we need IInputVocabularyTerm elements
            const vocTerm: IInputVocabularyTerm = {
              id: p.id,
              name: p.value,
              color: p.color,
            };
            return vocTerm;
          }),
          // ...or take them from the database (possible if we save freshly created terms right away to the database)
          // orgPropertyType.properties.map((p) => {
          //   // we need IInputVocabularyTerm elements
          //   return {
          //     id: p.id,
          //     name: p.value,
          //     color: p.color,
          //   };
          // }),
        );
      });
    }
  }

  handleSubmission = (values): Promise<any> => {
    const vocTerms: IInputVocabularyTerm[] = values[NewPhenotypeTagDialogImpl.FIELD_TEXT_VALUES];
    const valueType: string = values[NewPhenotypeTagDialogImpl.FIELD_TYPE].value;

    const predefinedProperties: InputOrganismPropertyInput[] = (vocTerms || []).map((vocTerm: IInputVocabularyTerm) => {
      const orgProperty: InputOrganismPropertyInput = {
        opoId: vocTerm.id,
        value: vocTerm.name,
        colour: vocTerm.color,
      };
      return orgProperty;
    });

    const groupName: string = values[NewPhenotypeTagDialogImpl.FIELD_GROUP_NAME]?.label;

    const payload: InputOrganismPropertyTypeInput = {
      id: this.props.propertyTypeIdToEdit,
      name: values[NewPhenotypeTagDialogImpl.FIELD_NAME].trim(),
      description: values[NewPhenotypeTagDialogImpl.FIELD_DESCRIPTION]?.trim(),
      group: groupName ? ({ name: groupName } as InputOrganismPropertyGroupInput) : undefined,
      isDiscrete: true,
      valueType: valueType,
      predefinedOrgProperties: predefinedProperties,
    };

    if (!this.props.propertyTypeIdToEdit && this.checkNameAlreadyExists(payload.name, groupName)) {
      throw new SubmissionError({
        _error: `A phenotype tag with this name already exists in the ${groupName ? "specified" : "default"} group.`,
      });
    }

    return this.props.saveOrgPropertyType(payload).then(
      (fulfilled) => {
        // make sure the dialog is closed, in production mode, this does not work sometimes without the timeout
        setTimeout(() => {
          this.props.close && this.props.close();
          this.props.reset();
          this.props.destroy();
        }, 100);
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  checkNameAlreadyExists = (name: string, groupName = ""): boolean => {
    const matchingOrgPropertyType = this.props.existingOrgPropertyTypes.find(
      (o) =>
        o.name.toUpperCase() === name?.toUpperCase() &&
        ((groupName.length === 0 && o.group.opgId === DEFAULT_ORG_PROPERTY_GROUP) ||
          (groupName.length > 0 && o.group.name.toUpperCase() === groupName.toUpperCase())),
    );

    return !!matchingOrgPropertyType && matchingOrgPropertyType.id !== this.props.propertyTypeIdToEdit;
  };

  handleSelectedDefaultTermsChanged = (terms: IInputVocabularyTerm[]) => {
    this.props.change(NewPhenotypeTagDialogImpl.FIELD_TEXT_VALUES, terms);
  };

  getPhenotypeGroups = () => {
    const groups: ISelectOption[] = uniqBy(
      this.props.existingOrgPropertyTypes.filter((type) => type.group.name !== "default"),
      "group.name",
    ).map((item: OrganismPropertyType) => {
      return {
        label: item.group.name,
        value: item.group.name,
      };
    });

    return sortBy(groups, (group: ISelectOption) => group.label.toLowerCase());
  };

  validateNewOption: Validator = (value: ISelectOption, _allValues?: any, _props?: any) => {
    const maxLength = DB_FIELD_MAX_LENGTH.ORGANISM_PROPERTY.GROUP_NAME;
    if (value?.label.length > maxLength) {
      return `The group name may not exceed ${maxLength} characters in length.`;
    }
    return specialCharacterValidation(value?.label);
  };

  render() {
    const textTypeSelected: boolean =
      this.props.currentTypeSelection && this.props.currentTypeSelection.value === ORG_PROPERTY_VALUE_TYPE.FREE;

    const valueTypeTooltip: string = this.props.propertyTypeIdToEdit
      ? "You can't change the value type of an existing phenotype."
      : null;

    return (
      <Dialog
        closeFn={this.props.close}
        mainActionButtonProps={{
          isLoading: this.props.submitting || this.props.isLoadingOrgPropertyTypes,
          isDisabled: this.props.submitting,
          children: this.props.propertyTypeIdToEdit ? "Save Phenotype" : "Create Phenotype",
          onPress: this.props.handleSubmit(this.handleSubmission),
        }}
        footerMessage={{ message: this.props.error }}
      >
        <FocusScope contain restoreFocus autoFocus>
          <InputReduxFormField
            componentClass="input"
            name={NewPhenotypeTagDialogImpl.FIELD_NAME}
            label={"Name"}
            maxLength={DB_FIELD_MAX_LENGTH.ORGANISM_PROPERTY.TYPE_NAME}
            placeholder={`Name of the new phenotype`}
            component={InputField}
            validate={[simpleFieldValidation, specialCharacterValidation]}
          />

          <InputReduxFormField
            componentClass="input"
            name={NewPhenotypeTagDialogImpl.FIELD_DESCRIPTION}
            label={"Description"}
            maxLength={DB_FIELD_MAX_LENGTH.ORGANISM_PROPERTY.DESCRIPTION}
            placeholder={`Optional description of the new phenotype`}
            component={InputField}
          />

          <ReactSelectReduxFormField
            name={NewPhenotypeTagDialogImpl.FIELD_GROUP_NAME}
            label="Group name"
            options={this.getPhenotypeGroups()}
            component={ReactSelectField}
            tooltip={"Group name of the new phenotype or leave empty for default group"}
            isClearable={true}
            isSearchable={true}
            creatable={true}
            validate={this.validateNewOption}
          />

          <ReactSelectReduxFormField
            name={NewPhenotypeTagDialogImpl.FIELD_TYPE}
            label="Value type"
            options={VALUE_TYPE_OPTIONS}
            component={ReactSelectField}
            isClearable={false}
            isSearchable={false}
            isDisabled={valueTypeTooltip}
            tooltip={valueTypeTooltip}
          />

          {textTypeSelected && (
            <ControlledValuesManager
              controlledValues={this.props.currentDefaultTermSelection}
              handleControlledValuesChanged={this.handleSelectedDefaultTermsChanged}
              label={"Vocabulary terms"}
              maxTermLength={DB_FIELD_MAX_LENGTH.ORGANISM_PROPERTY.NAME}
              deleteDisabled={!!this.props.propertyTypeIdToEdit}
              showColorColumn
            />
          )}
        </FocusScope>
      </Dialog>
    );
  }
}

const NewPhenotypeTagDialog = connect<
  INewPhenotypeTagDialogAppStateProps,
  INewPhenotypeTagDialogDispatchProps,
  INewPhenotypeTagDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: NewPhenotypeTagDialogImpl.FORM_NAME,
    touchOnBlur: false,
  })(NewPhenotypeTagDialogImpl as any) as any,
);

export default NewPhenotypeTagDialog;
